import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

interface NotificationBadgeContextType {
  unreadCount: number;
  loading: boolean;
  refresh: () => Promise<void>;
}

const NotificationBadgeContext = createContext<NotificationBadgeContextType | undefined>(undefined);

/**
 * Provider pour gérer le badge de notification
 * Principe KISS : Logique simple et directe
 * Principe SOLID : Single Responsibility - Gère uniquement le compteur de notifications non lues
 */
export const NotificationBadgeProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  /**
   * Fonction simple pour récupérer le nombre de notifications non lues
   * Utilise une requête directe et claire
   */
  const refresh = useCallback(async () => {
    if (!user) {
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    try {
      const { count, error } = await supabase
        .from("notifications")
        .select("id", { count: "exact", head: true })
        .eq("user_id", user.id)
        .eq("is_read", false);

      if (error) {
        console.error("[NotificationBadge] Erreur lors de la récupération:", error);
        return;
      }

      const newCount = count ?? 0;
      console.log(
        "[NotificationBadge] Nombre de notifications non lues:",
        newCount,
        "pour user:",
        user.id
      );
      setUnreadCount(newCount);
    } catch (error) {
      console.error("[NotificationBadge] Exception:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Initialiser et s'abonner aux changements
  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    // Charger le compteur initial
    refresh();

    // S'abonner aux changements en temps réel
    const channel = supabase
      .channel(`notifications-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          // Rafraîchir immédiatement à chaque changement
          refresh();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, refresh]);

  return (
    <NotificationBadgeContext.Provider value={{ unreadCount, loading, refresh }}>
      {children}
    </NotificationBadgeContext.Provider>
  );
};

/**
 * Hook simple pour accéder au badge de notification
 */
export const useNotificationBadge = () => {
  const context = useContext(NotificationBadgeContext);
  if (context === undefined) {
    throw new Error("useNotificationBadge must be used within a NotificationBadgeProvider");
  }
  return context;
};
